import { useCallback, useState } from 'react';

import type {
  AdvancedFilter,
  FilterState,
  QuickFilterDef,
  QuickFilterValue,
} from './FilterBar.props';
import { emptyQuickState } from './filterBarState';
import { useFilterDirty } from '../../../hooks/useFilterDirty';

const DEFAULT_FILTER_STATE: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: [],
  contractNames: [],
  extraText: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
};

export interface UseFilterBarStateOptions {
  onSearch: (filters: FilterState) => void;
  initialFilters?: Partial<FilterState>;
  /** Present on the config-driven path; seeds the empty slot bag. */
  quickFilters?: QuickFilterDef[];
  /** What Clear resets to. Defaults to everything empty. */
  clearFilters?: FilterState;
}

/**
 * The bar's own filter state and every mutation of it.
 *
 * Pulled out of `FilterBar` so that component stays a layout concern and both
 * files stay inside the project's 250-line budget.
 *
 * Note this state is deliberately uncontrolled: it seeds once from
 * `initialFilters`, and pages that need to push state back in remount the bar
 * via a `filterBarKey`. Converting it to a controlled `value`/`onChange` would
 * be a real improvement but is a separate change — doing it here would double
 * the risk surface of the config migration for no deletion benefit.
 */
export const useFilterBarState = ({
  onSearch,
  initialFilters,
  quickFilters,
  clearFilters,
}: UseFilterBarStateOptions) => {
  const [filters, setFilters] = useState<FilterState>({
    ...DEFAULT_FILTER_STATE,
    ...(quickFilters ? { quick: emptyQuickState(quickFilters) } : {}),
    ...initialFilters,
  });
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);
  const { isDirty, markApplied } = useFilterDirty(filters);

  /** Applies `next` and runs the search immediately. Used by the paths where
   *  waiting for a manual Search would leave a removed filter still applied. */
  const applyNow = useCallback(
    (next: FilterState) => {
      setFilters(next);
      onSearch(next);
      markApplied(next);
    },
    [onSearch, markApplied]
  );

  const updateFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateQuick = useCallback((key: string, value: QuickFilterValue) => {
    setFilters((prev) => ({ ...prev, quick: { ...prev.quick, [key]: value } }));
  }, []);

  const handleSearch = useCallback(() => {
    onSearch(filters);
    markApplied(filters);
  }, [filters, onSearch, markApplied]);

  // Adding via the modal keeps today's behaviour: the filter applies once the
  // user clicks Search.
  const handleApplyFilter = useCallback((filter: AdvancedFilter) => {
    setFilters((prev) => ({ ...prev, advancedFilters: [...prev.advancedFilters, filter] }));
    setRecentlyUsed((prev) =>
      prev.includes(filter.dataKey) ? prev : [filter.dataKey, ...prev].slice(0, 5)
    );
  }, []);

  // Editing a chip replaces it in place and re-runs the search, mirroring
  // remove — an edit that needed a second click to take effect reads as broken.
  const handleEditFilter = useCallback(
    (index: number, filter: AdvancedFilter) => {
      if (index >= filters.advancedFilters.length) return;
      applyNow({
        ...filters,
        advancedFilters: filters.advancedFilters.map((f, i) => (i === index ? filter : f)),
      });
    },
    [filters, applyNow]
  );

  // Removing re-runs the search so the deleted filter stops applying straight
  // away rather than lingering until a manual Search or a refresh.
  const handleRemoveFilter = useCallback(
    (index: number) => {
      applyNow({
        ...filters,
        advancedFilters: filters.advancedFilters.filter((_, i) => i !== index),
      });
    },
    [filters, applyNow]
  );

  // Resets everything the bar manages — including the legacy saved-report
  // carry-over — rather than the chips-only reset the old link gave. Pages with
  // a non-empty baseline (Structures' Active, Parts' Serialised) pass
  // `clearFilters`; without it Clear would silently discard that baseline.
  const handleClearAll = useCallback(() => {
    applyNow(clearFilters ? { ...clearFilters } : { ...DEFAULT_FILTER_STATE });
  }, [applyNow, clearFilters]);

  return {
    filters,
    recentlyUsed,
    isDirty,
    updateFilter,
    updateQuick,
    handleSearch,
    handleApplyFilter,
    handleEditFilter,
    handleRemoveFilter,
    handleClearAll,
  };
};
